<template>
  <div class="practice-product-card" :class="[{ 'shrink-card': isShrink }, greyOut ? 'inactive-card' : '']">
    <div v-if="row.originalPrice != row.currentPrice" class="promo-tag">
      {{ row.price_after_promo }}
    </div>

    <div class="card-image-area">
      <div v-if="imageSrc" class="product-image">
        <img :src="imageSrc" :alt="row.name" />
        <div v-if="greyOut" class="image-overlay"></div>
      </div>
      <div v-else class="placeholder-image" :class="greyOut ? 'inactive-bg' : 'active-bg'">
        <img v-if="!greyOut" src="@/assets/markup/vibrant_logo_color.svg" alt="logo" />
        <img v-else src="@/assets/markup/vibrant_logo_bw.svg" alt="logo" />
      </div>
    </div>

    <div class="card-content">
      <el-tooltip v-if="row.name && row.name.length > 45" popper-class="merch-card-popper" :content="row.name" :showAfter="300">
        <div class="card-title" :class="greyOut ? 'disabled-name' : ''">{{ row.name }}</div>
      </el-tooltip>
      <div v-else class="card-title" :class="greyOut ? 'disabled-name' : ''">{{ row.name }}</div>

      <el-tooltip
        v-if="row.description && row.description.length > 32"
        popper-class="merch-card-popper"
        :content="row.description"
        :showAfter="300"
      >
        <div class="card-description">{{ row.description }}</div>
      </el-tooltip>
      <div v-else class="card-description">{{ row.description }}</div>

      <div class="card-details">
        <div class="detail-row">
          <span class="detail-key">{{ $t("OrderPage[\"Original Price\"]") }}</span>
          <span class="detail-value" :class="[row.originalPrice != row.currentPrice ? 'line-through' : '', greyOut ? 'disabled-name' : '']">
            {{ formattedPrice }}
          </span>
        </div>
        <div class="detail-row">
          <span class="detail-key">{{ $t("OrderPage[\"Type\"]") }}</span>
          <span class="detail-value" :class="greyOut ? 'disabled-name' : ''">{{ formattedType }}</span>
        </div>
        <div class="detail-row">
          <span class="detail-key">{{ $t("OrderPage[\"Return days\"]") }}</span>
          <span class="detail-value" :class="greyOut ? 'disabled-name' : ''">{{ returnDays }}</span>
        </div>
      </div>
    </div>

    <div v-if="!disabled" class="down-container" :class="quantity === 0 ? 'add-to-cart-container' : (!row.editable ? 'grey' : '')">
      <div v-if="quantity === 0" class="add-to-cart-btn" :class="!row.editable ? 'disabled' : ''" @click="addQuantity">
        {{ $t("OrderPage[\"Add to Cart\"]") }}
      </div>
      <div v-else class="quantity-control">
        <div class="control-btn minus-btn" :class="!row.editable ? 'disabled' : ''" @click="decreaseQuantiy">
          <span class="control-symbol">-</span>
        </div>
        <div class="ordered-count" :class="!row.editable ? 'disabled-name' : ''">{{ quantity }}</div>
        <div class="control-btn plus-btn" :class="reachToMax || !row.editable ? 'disabled' : ''" @click="addQuantity">
          <span class="control-symbol">+</span>
        </div>
      </div>
    </div>
    <div v-else class="down-container disabled-down-container">
      <el-tooltip popper-class="merch-card-popper" :content="tooltipMessage">
        <div class="add-btn disabled-name disabled">
          {{ $t("OrderPage[\"Add to Cart\"]") }}
        </div>
      </el-tooltip>
    </div>
  </div>
</template>

<script>
import { mapState } from "vuex";
import { downloadFile } from '@/api/markup';

export default {
  props: {
    row: {
      type: Object,
      default: () => {},
    },
    isShrink: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      imageSrc: null,
    }
  },

  computed: {
    ...mapState("orderTest/order", ["paymentMethod"]),
    ...mapState("orderTest/merchandise", ["selectedMerchMap"]),
    ...mapState("orderTest/user", ["userInfo"]),
    ...mapState("orderTest", ["defaultSettings"]),

    id() {
      return this.row ? this.row.id : -1;
    },

    quantity() {
      if (this.id === -1 || !this.selectedMerchMap[this.id]) {
        return 0;
      }
      return this.selectedMerchMap[this.id].quantity;
    },

    formattedPrice() {
      return this.row.price;
    },
    formattedType() {
      return this.row.type.charAt(0).toUpperCase() + this.row.type.slice(1);
    },
    returnDays() {
      return this.row.returnDaysNum ? this.row.returnDaysNum : this.$t("OrderPage[\"No return\"]");
    },

    isEditOrder() {
      return window.location.href.includes("editOrderSampleId");
    }, 
    isAddOnOrder() {
      return window.location.href.includes("addOnSampleId");
    },

    effectivePaymentMethod() {
      return this.paymentMethod || this.defaultSettings?.chargeMethod || "";
    },

    disabled() {
      if (this.isEditOrder || this.isAddOnOrder) {
        return !this.row.isInEditOrder;
      }
      return (!this.effectivePaymentMethod || this.effectivePaymentMethod === "customerPay");
    },

    greyOut() {
      return this.disabled || !this.row.editable;
    },  

    tooltipMessage() {
      if (this.isAddOnOrder) {
        return this.$t("OrderPage[\"You can't add practice product in Modify Order\"]");
      } else if (this.isEditOrder) {
        return this.$t("OrderPage[\"You can't add practice product in Update Order\"]");
      } else {
        return this.$t("OrderPage[\"Please choose Patient Pay Later or Patient Pay Now to enable practice product\"]");
      }
    },

    reachToMax() {
      if (this.isEditOrder || this.isAddOnOrder) {
        return this.quantity === this.row.quantityInEditOrder;
      }
      return false;
    }
  },
  watch: {
    async 'row.image'(newVal, oldVal) {
      if (!newVal) {
        this.imageSrc = null;
        return;
      }
      await this.getImageSrc();
    }
  },
  mounted() {
    if (this.row && this.row.image) {
      this.getImageSrc();
    }
  },
  beforeUnmount() {
    if (this.imageSrc) {
      URL.revokeObjectURL(this.imageSrc);
    }
  },
  methods: {
    decreaseQuantiy() {
      if (this.disabled  || !this.row.editable) {
        return;
      }
      this.$store.dispatch("orderTest/merchandise/removeFromSelectedMerchMap", this.id);
    },
    addQuantity() {
      if (this.disabled  || !this.row.editable || this.reachToMax) {
        return;
      }
      // if (this.row.stock > 0 && this.quantity === this.row.stock) {
      //   this.$notify(getErrorMessageObj("Maximum quantity reached for this item.")); 
      //   return;
      // }
      this.$store.dispatch("orderTest/merchandise/addToSelectedMerchMap", this.id);
    },
    async getImageSrc() {
      if (!this.row.image) {
        return;
      }
      try {
        const parts = this.row.image.split("/");
        const folderName = parts[0];
        const fileName = parts[parts.length - 1];

        const { data } = await downloadFile(folderName, fileName);
        this.imageSrc = URL.createObjectURL(data);
      } catch (error) {
        // console.error("Error downloading image:", error);
        this.imageSrc = null;
      }
    },
  }
}
</script>

<style scoped>
.practice-product-card {
  min-width: 222px;
  height: 332px;
  padding: 10px;
  border-radius: 8px;
  border: 1px solid #e6eaf0;
  background: #fff;
  box-shadow: 0 1px 15px rgba(164, 164, 164, 0.18);
  display: flex;
  flex-direction: column;
  gap: 8px;
  position: relative;
}

.shrink-card {
  max-width: 222px;
}

.inactive-card {
  background: #f9fbfd;
  border-color: #e0e0e0;
}

.promo-tag {
  position: absolute;
  top: 8px;
  left: 8px;
  z-index: 3;
  background: #ff6d6d;
  color: #fff;
  font-size: 12px;
  font-weight: 600;
  border-radius: 999px;
  padding: 2px 10px;
  line-height: 18px;
}

.card-image-area {
  height: 88px;
  width: 100%;
  border-radius: 6px;
  overflow: hidden;
  position: relative;
}

.placeholder-image {
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
}

.placeholder-image img {
  height: 56px;
  width: auto;
}

.active-bg {
  background: #f9fbfd;
}

.inactive-bg {
  background: #f2f2f2;
}

.product-image {
  width: 100%;
  height: 100%;
  position: relative;
}

.product-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.image-overlay {
  position: absolute;
  inset: 0;
  background: rgba(255, 255, 255, 0.4);
}

.card-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.card-title {
  color: #333;
  font-family: Inter, sans-serif;
  font-size: 16px;
  font-weight: 500;
  line-height: 20px;
  width: 100%;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  overflow: hidden;
  text-overflow: ellipsis;
  word-break: break-word;
  overflow-wrap: anywhere;
}

.card-description {
  color: #979797;
  font-family: Inter, sans-serif;
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.card-details {
  margin-top: 6px;
  border-top: 1px solid #e0e0e0;
  padding-top: 6px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.detail-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.detail-key {
  color: #979797;
  font-size: 13px;
  line-height: 18px;
}

.detail-value {
  color: #666;
  font-size: 13px;
  line-height: 18px;
}

.down-container {
  height: 34px;
  border-radius: 4px;
  border: 1px solid #d7dde6;
  background: #ffffff;
  display: flex;
  align-items: center;
  overflow: hidden;
}

.quantity-control {
  display: grid;
  grid-template-columns: 1fr 2fr 1fr;
  width: 100%;
  height: 100%;
}

.add-btn {
  color: #20a7c1;
  text-align: center;
  font-weight: 500;
  width: 100%;
}

.add-to-cart-container {
  border: none !important;
  background: transparent !important;
  padding: 0;
}

.add-to-cart-btn {
  background: #004879;
  color: #fff;
  text-align: center;
  font-weight: 500;
  font-size: 14px;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  cursor: pointer;
  transition: background 0.2s;
}

.add-to-cart-btn:hover:not(.disabled) {
  background: #003a5f;
}

.add-to-cart-btn.disabled {
  background: #979797;
  cursor: not-allowed;
}

.ordered-count {
  height: 100%;
  border-left: 1px solid #d7dde6;
  border-right: 1px solid #d7dde6;
  font-weight: 600;
  color: #333;
  display: flex;
  justify-content: center;
  align-items: center;
}

.disabled-name.ordered-count {
  border-left: 1px solid var(--color-Grey-Grey-97, #979797);
  border-right: 1px solid var(--color-Grey-Grey-97, #979797);
}

.control-btn {
  height: 100%;
  min-width: 40px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  background: #004879;
}

.control-symbol {
  font-size: 18px;
  line-height: 1;
  color: #fff;
  font-weight: 600;
}

.disabled {
  cursor: not-allowed;
}

.disabled-name {
  color: #979797;
}

.disabled-down-container {
  border: 1px solid var(--color-Grey-Grey-E6, #E6E6E6);
  background: var(--color-Grey-Grey-E6, #E6E6E6);
}

.img-mask {
  position: absolute;
  inset: 0;
  background: linear-gradient(0deg, rgba(255, 255, 255, 0.40) 0%, rgba(255, 255, 255, 0.40) 100%);
  border-radius: 5.056px;
  pointer-events: none;
}

.down-container.grey {
  border: 1px solid var(--color-Grey-Grey-97, #979797);
  background: #f2f2f2;
}

.disabled .material-icons {
  color: #979797;
}

.disabled .control-symbol {
  color: #979797;
}
</style>